import { Badge, Box, Button, HStack, Spinner, Tag, TagLabel, Text, Wrap, WrapItem } from "@chakra-ui/react";
import { PencilSimple } from "@phosphor-icons/react";
import React, { useState } from "react";
import { formatDate } from "utils";
import { useRightSidebar } from "context/RightSidebarContext";
import AITaskDetailsPanel from "./AITaskDetailsRSidebar";
import { useQuery } from "react-query";
import { taskService } from "api/ai/ai.service";
import AIProgressWidget from "./AIProgressWidget";
import { useSelector } from "react-redux";
import PageHeader from "components/text/PageHeader";
import AITasksTable from "./AITasksTable";
import { TASK_TYPE_LABELS } from "./taskTypeLabels";

/**
 * List of AI Tasks for debugging / internal use
 * @returns
 */
export default function AITasksListPanel() {
  const { setRightSidebar, doOpen } = useRightSidebar();
  const allStatuses = ["done", "running", "queued", "failed", "cancelled"];
  const [statusFilter, setStatusFilter] = useState(allStatuses);
  const [editing, setEditing] = useState(false);
  // Fetch all tasks, filter client-side so we know which statuses have data
  const { data: allTasks, isLoading } = useQuery(["apiListTasks"],
	() => taskService.list({ size: 20 }), {
    cacheTime: 0,
    refetchInterval: 10000,
  });
  const statusesWithData = new Set(allTasks?.map((t) => t.status));
  const isAllSelected = statusFilter.length === allStatuses.length;
  const aiTasks = isAllSelected ? allTasks : allTasks?.filter((t) => statusFilter.includes(t.status));

  const roundSelector = useSelector((state) => state.issbRound);
  const rounds = roundSelector?.roundList;
  const name4round = (roundId) => {
    const round = rounds.find((r) => r.id === roundId);
    return round?.name;
  };

  const openSidebar = (item) => {
    setRightSidebar(<AITaskDetailsPanel task={item} />);
    doOpen();
  };

  const handleRowClick = (row) => {
    openSidebar(row.original);
  };

  /** @type {ColumnDef[]} */
  const columns = [
    {
      header: "Type",
      accessorKey: "task_type",
      cell: ({ row }) => <Text>{TASK_TYPE_LABELS[row.original.task_type] || row.original.task_type}</Text>,
      size: 120,
    },
    {
      header: "Round",
      accessorKey: "round_id",
      accessorFn: (row) => name4round(row.round_id) || row.round_id,
      cell: ({ cell }) => <Text>{cell.getValue()}</Text>,
    },
    {
      header: "Status",
      accessorKey: "status",
      size: 80,
      cell: ({ row }) => {
        const status = row.original.status;
        const colorMap = { done: "green", running: "blue", queued: "orange", failed: "red", cancelled: "gray" };
        return (
          <Badge colorScheme={colorMap[status] || "gray"} borderRadius="md" px={2} fontSize="xs">
            {status}
          </Badge>
        );
      },
    },
    {
      header: "Progress",
      cell: ({ row }) => <AIProgressWidget task={row.original} refetchInterval={5000} />,
      size: 80,
    },
    {
      header: "Created",
      size: 90,
      accessorFn: (file) => formatDate(file.created_date),
      cell: ({ cell }) => <Text fontSize="xs" color="gray.500">{cell.getValue()}</Text>,
    },
    {
      header: "Completed",
      size: 90,
      accessorFn: (file) => formatDate(file.completed_date),
      cell: ({ cell }) => <Text fontSize="xs" color="gray.500">{cell.getValue() || "-"}</Text>,
    },
  ];


  return (
    <Box>
      <PageHeader
        title="AI Tasks"
        subTitle="Monitor and review AI task execution, progress, and status across your rounds."
      />
      {isAllSelected && !editing ? (
        <Wrap mt={6} spacing={2} align="center">
          <WrapItem>
            <Tag
              size="sm"
              borderRadius="full"
              bg="lightLime"
              border="1px solid"
              borderColor="lime"
              px={3}
              py={1}
              cursor="pointer"
              onClick={() => setEditing(true)}
            >
              <TagLabel>All statuses</TagLabel>
              <Box as="span" ml={2} display="inline-flex"><PencilSimple size={12} /></Box>
            </Tag>
          </WrapItem>
        </Wrap>
      ) : (
        <Wrap mt={6} spacing={2} align="center">
          {allStatuses.map((status) => {
            const isSelected = statusFilter.includes(status);
            const hasData = statusesWithData.has(status);
            return (
              <WrapItem key={status}>
                <Tag
                  size="sm"
                  borderRadius="full"
                  bg={isSelected && hasData ? "lightLime" : "gray.100"}
                  border="1px solid"
                  borderColor={isSelected && hasData ? "lime" : "gray.300"}
                  color={!hasData ? "gray.300" : isSelected ? "inherit" : "gray.400"}
                  px={3}
                  py={1}
                  cursor={hasData ? "pointer" : "default"}
                  opacity={hasData ? 1 : 0.6}
                  onClick={() => {
                    if (!hasData) return;
                    if (isSelected) {
                      const next = statusFilter.filter((s) => s !== status);
                      setStatusFilter(next.length === 0 ? allStatuses : next);
                    } else {
                      const next = [...statusFilter, status];
                      if (next.length === allStatuses.length) setEditing(false);
                      setStatusFilter(next);
                    }
                  }}
                  textTransform="capitalize"
                >
                  <TagLabel>{status}</TagLabel>
                  {hasData && <Box as="span" ml={1} fontSize="xs">{isSelected ? "✕" : "+"}</Box>}
                </Tag>
              </WrapItem>
            );
          })}
          <WrapItem>
            <Button
              variant="link"
              size="xs"
              color="gray.500"
              fontWeight="normal"
              onClick={() => {
                setStatusFilter(allStatuses);
                setEditing(false);
              }}
            >
              Select all
            </Button>
          </WrapItem>
        </Wrap>
      )}
      {!aiTasks && isLoading && <Spinner width={"100px"} height={"100px"} />}
      {aiTasks && (
        <Box mt={6}>
          <AITasksTable
            columns={columns}
            data={aiTasks}
            isLoading={isLoading}
            onRowClick={handleRowClick}
          />
        </Box>
      )}
    </Box>
  );
}
